import React from 'react';

import { css } from '@emotion/css';
import { Dialog, IconButton, Typography } from '@mui/material';
import { BiChevronLeft, BiChevronRight, BiX } from 'react-icons/bi';

// TYPES --------------------------------------------------------------------- //

export interface LightboxPhoto {
  url: string;
  label: string;
}

interface PhotoLightboxProps {
  open: boolean;
  photos: LightboxPhoto[];
  initialIndex?: number;
  onClose: () => void;
}

// COMPONENT ----------------------------------------------------------------- //

const PhotoLightbox = ({ open, photos, initialIndex = 0, onClose }: PhotoLightboxProps) => {
  const cls = useStyles();
  const [index, setIndex] = React.useState(initialIndex);

  // Reset index when photos change or lightbox opens
  React.useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  // Keyboard navigation
  React.useEffect(() => {
    if (!open || photos.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setIndex(prev => (prev + photos.length - 1) % photos.length);
      } else if (e.key === 'ArrowRight') {
        setIndex(prev => (prev + 1) % photos.length);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, photos.length, onClose]);

  if (photos.length === 0) return null;

  const safeIndex = Math.min(index, photos.length - 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-label='Photo lightbox'
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.92)' } },
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          maxWidth: '92vw',
          maxHeight: '92vh',
          overflow: 'visible',
          m: 0,
        },
      }}
    >
      <div className={cls.lightbox}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          className={cls.lightboxClose}
          size='small'
          aria-label='Close lightbox'
        >
          <BiX size={24} />
        </IconButton>

        {/* Label + counter */}
        <Typography className={cls.lightboxLabel}>
          {photos[safeIndex]?.label}
          <span style={{ opacity: 0.5, marginLeft: 8 }}>
            {safeIndex + 1} / {photos.length}
          </span>
        </Typography>

        {/* Image */}
        <img
          src={photos[safeIndex]?.url}
          alt={photos[safeIndex]?.label}
          className={cls.lightboxImage}
        />

        {/* Nav arrows */}
        {photos.length > 1 ? (
          <>
            <IconButton
              className={cls.lightboxPrev}
              onClick={() => setIndex((safeIndex + photos.length - 1) % photos.length)}
              aria-label='Previous photo'
            >
              <BiChevronLeft size={32} />
            </IconButton>
            <IconButton
              className={cls.lightboxNext}
              onClick={() => setIndex((safeIndex + 1) % photos.length)}
              aria-label='Next photo'
            >
              <BiChevronRight size={32} />
            </IconButton>
          </>
        ) : null}
      </div>
    </Dialog>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = () => ({
  lightbox: css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  lightboxLabel: css`
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    text-align: center;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  `,
  lightboxImage: css`
    max-width: 88vw;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 4px;
    will-change: transform;
    transform: translateZ(0);
  `,
  lightboxClose: css`
    position: absolute;
    top: -36px;
    right: -4px;
    color: rgba(255, 255, 255, 0.7);
    &:hover {
      color: #fff;
    }
  `,
  lightboxPrev: css`
    position: absolute;
    left: -52px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.7);
    &:hover {
      color: #fff;
    }
  `,
  lightboxNext: css`
    position: absolute;
    right: -52px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.7);
    &:hover {
      color: #fff;
    }
  `,
});

export default PhotoLightbox;
